/**
 * Image montrée par le MJ : Realtime DB (imagePanel/{campagne}) et Storage (panel/{campagne}.png),
 * aux mêmes emplacements qu'Aria. API modulaire.
 */
import {getDatabase, ref as dbRef, set, update, onValue} from 'firebase/database';
import {getStorage, ref as storageRef, uploadBytes, getDownloadURL} from 'firebase/storage';
import {fitWithin} from './scene';

// Limite historique d'Aria pour ce chemin : les images plus lourdes sont recompressées en JPEG
export const MAX_SCENE_BYTES = 800 * 1024;
const MAX_SIDE = 1920;
const QUALITIES = [0.85, 0.75, 0.6, 0.45];

const sceneRef = (campaignUid) => dbRef(getDatabase(), `imagePanel/${campaignUid}`);

/** @returns {Function} désabonnement */
export const watchScene = (campaignUid, onChange) => onValue(sceneRef(campaignUid), snapshot => onChange(snapshot.val()));

export const showScene = (campaignUid, url, memberAvailable, caption = '') => set(sceneRef(campaignUid), {
  url,
  memberAvailable,
  immersive: true,
  caption: caption.trim() || null,
  shownAt: Date.now(),
});

export const hideScene = (campaignUid) => update(sceneRef(campaignUid), {immersive: false});

const toBlob = (canvas, quality) => new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));

/** Réduit et recompresse l'image si elle dépasse la limite ; null si elle reste trop lourde. */
const compress = async (file) => {
  if (file.size <= MAX_SCENE_BYTES) return file;
  const bitmap = await createImageBitmap(file);
  const {width, height} = fitWithin(bitmap.width, bitmap.height, MAX_SIDE);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
  for (const quality of QUALITIES) {
    const blob = await toBlob(canvas, quality);
    if (blob && blob.size <= MAX_SCENE_BYTES) return blob;
  }
  return null;
};

/**
 * Envoie l'image et renvoie son URL publique (avec `v` pour contourner le cache : même chemin à chaque envoi).
 * @throws {Error} 'tooBig' si l'image reste trop lourde après compression
 */
export const uploadSceneImage = async (campaignUid, file) => {
  const blob = await compress(file);
  if (!blob) throw new Error('tooBig');
  const imageRef = storageRef(getStorage(), `panel/${campaignUid}.png`);
  await uploadBytes(imageRef, blob, {contentType: blob.type || 'image/jpeg'});
  const url = await getDownloadURL(imageRef);
  return `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
};
